import { Content, Portal, Root, Trigger } from "@radix-ui/react-popover";
import type * as React from "react";
import {
  useCallback,
  useEffect,
  useId,
  useMemo,
  useRef,
  useState,
} from "react";
import { useTranslation } from "react-i18next";
import { tv } from "tailwind-variants";
import { Icon, Tag } from "../../..";
import { useIsMobile } from "../../../hooks";
import { cn } from "../../../util/classNameConstructor";
import type { TagProps } from "../../atoms/tag/Tag";
import ListItem from "../../core/list/ListItem";
import Seperator from "../../core/seperator/Seperator";
import ModalButton from "../../molecules/modalStack/ModalButton";
import { useModals } from "../../molecules/modalStack/ModalProvider";
import type { IconName } from "../icon/names";
import { Input, type StringInputProps } from "./Input";

type ComboboxOptionBase = {
  value: string;
  label: string;
  subLabel?: string;
  disabled?: boolean;
  leadingIcon?: IconName | React.ReactNode;
  trailingIcon?: IconName | React.ReactNode;
  onClick?: (e: React.MouseEvent<HTMLDivElement>) => void;
  data?: unknown;
  color?: TagProps["color"];
};
export type ComboboxOption =
  | ComboboxOptionBase
  | (Partial<ComboboxOptionBase> & {
      divider?: boolean;
    });

// Type guard functions with proper null safety
const isDividerOption = (
  option?: ComboboxOption | null,
): option is { divider: boolean } => {
  return Boolean(option && typeof option === "object" && "divider" in option);
};

const isRegularOption = (
  option?: ComboboxOption | null,
): option is ComboboxOptionBase => {
  return Boolean(
    option && typeof option === "object" && !isDividerOption(option),
  );
};

// Helper to safely get label from selected option
const getOptionLabel = (option?: ComboboxOption | null): string => {
  return isRegularOption(option) ? option.label : "";
};

// Helper to check if option is selectable (not divider and not disabled)
const isSelectableOption = (option?: ComboboxOption | null): boolean => {
  return isRegularOption(option) && !option.disabled;
};

export type ComboboxProps = Omit<
  StringInputProps,
  | "value"
  | "onChange"
  | "type"
  | "step"
  | "min"
  | "max"
  | "inputMode"
  | "pattern"
> & {
  options: ComboboxOption[];
  onValueChange?: (value: string) => void;
  emptyMessage?: string;
  loading?: boolean;
  dropdownRef?: React.RefObject<HTMLDivElement>;
  ref?: React.RefObject<HTMLInputElement | null>;
  open?: boolean;
  onOpenChange?: React.Dispatch<React.SetStateAction<boolean>>;
  enableSearch?: boolean;
  classNames?: {
    optionLeadingIcon?: string;
  };
  asModal?: boolean;
} & (
    | {
        multiple: true;
        value?: string[] | null;
        onChange?: (option: ComboboxOption[]) => void;
      }
    | {
        multiple?: false;
        value?: string | null;
        onChange?: (option: ComboboxOption) => void;
      }
  );

const popoverContentVariants = tv({
  base: "z-[9999999999999] bg-surface-default w-[var(--radix-popover-trigger-width)] rounded-xl border text-popover-foreground shadow-lg outline-none duration-300 transition-opacity",
  variants: {
    isEmpty: {
      true: "opacity-0",
      false: "",
    },
  },
  defaultVariants: {
    isEmpty: false,
  },
});

const optionVariants = tv({
  base: "group/option relative flex items-center w-full gap-1.5 cursor-default select-none px-2 py-1.5 min-h-10 text-base outline-none transition-colors rounded-lg",
  variants: {
    disabled: {
      true: "pointer-events-none opacity-50",
      false: "cursor-pointer hover:bg-surface-hovered",
    },
    highlighted: {
      true: "bg-surface-hovered group-hover/listbox:bg-surface-highlight-subdued group-hover/listbox:hover:bg-surface-hovered",
      false: "",
    },
    divider: {
      true: "pointer-events-none py-0 px-0 min-h-0 justify-center",
      false: "",
    },
  },
  defaultVariants: {
    disabled: false,
    highlighted: false,
    divider: false,
  },
});

export const Combobox: React.FC<ComboboxProps> = ({
  options,
  value = "",
  onChange,
  onValueChange,
  loading = false,
  emptyMessage = "No options found.",
  dropdownRef,
  ref,
  open: openProp = false,
  onOpenChange,
  leadingIcon,
  trailingIcon: trailingIconProp,
  trailingIconProps: trailingIconPropsProp,
  multiple = false,
  enableSearch = true,
  classNames,
  placeholder,
  asModal = false,
  ...props
}) => {
  const { t } = useTranslation();
  const isMobile = useIsMobile();
  const { closeModal } = useModals();
  const [open, setOpen] = useState(openProp);
  const [isFocusVisible, setIsFocusVisible] = useState(false);
  const [pendingBlurCheck, setPendingBlurCheck] = useState(false);
  const justSelected = useRef(false);

  // Helper function to get selected options from value
  const getSelectedOptions = useCallback(
    (val: string | string[] | null | undefined): ComboboxOption[] => {
      if (!val) return [];

      const findOption = (searchValue: string) =>
        options.find(
          (opt) =>
            isRegularOption(opt) &&
            (opt.value === searchValue || opt.label === searchValue),
        );

      if (multiple && Array.isArray(val)) {
        return val.map(findOption).filter(Boolean) as ComboboxOption[];
      }

      const currentValue = findOption(val.toString());
      return currentValue ? [currentValue] : [];
    },
    [options, multiple],
  );

  // Selected state and input value
  const [selected, setSelected] = useState<ComboboxOption[]>(() =>
    getSelectedOptions(value),
  );

  const [inputValue, setInputValue] = useState(() => {
    if (multiple) return "";
    return (
      getOptionLabel(getSelectedOptions(value)[0]) || value?.toString() || ""
    );
  });

  // Navigation helpers with simplified, more reliable logic
  const navigationHelpers = useMemo(() => {
    const findNext = (fromIndex: number): number => {
      // Search forward from fromIndex+1 to end (skip current index)
      for (let i = fromIndex + 1; i < options.length; i++) {
        if (isSelectableOption(options[i])) return i;
      }
      // Wrap around: search from start to fromIndex
      for (let i = 0; i <= fromIndex; i++) {
        if (isSelectableOption(options[i])) return i;
      }
      return -1;
    };

    const findPrev = (fromIndex: number): number => {
      // Search backward from fromIndex-1 to start (skip current index)
      for (let i = fromIndex - 1; i >= 0; i--) {
        if (isSelectableOption(options[i])) return i;
      }
      // Wrap around: search from end to fromIndex
      for (let i = options.length - 1; i >= fromIndex; i--) {
        if (isSelectableOption(options[i])) return i;
      }
      return -1;
    };

    const findFirst = (): number => {
      return options.findIndex(isSelectableOption);
    };

    const findLast = (): number => {
      for (let i = options.length - 1; i >= 0; i--) {
        if (isSelectableOption(options[i])) return i;
      }
      return -1;
    };

    return { findNext, findPrev, findFirst, findLast };
  }, [options]);

  const matchingIndex = useMemo(
    () =>
      options.findIndex(
        (option) =>
          isRegularOption(option) &&
          option.label === inputValue &&
          !option.disabled,
      ),
    [options, inputValue],
  );

  const [highlightedIndex, setHighlightedIndex] = useState(() => {
    return matchingIndex > -1 ? matchingIndex : navigationHelpers.findFirst();
  });

  const inputRefFallback = useRef<HTMLInputElement>(null);
  const inputRef = ref || inputRefFallback;
  const listRef = useRef<HTMLDivElement>(null);
  const comboboxId = useId();
  const listboxId = `${comboboxId}-listbox`;

  // Consolidated blur/input validation logic
  const validateInput = useCallback(
    (deferIfLoading = false) => {
      if (loading && deferIfLoading) {
        setPendingBlurCheck(true);
        return;
      }

      if (multiple) {
        setInputValue("");
        onValueChange?.("");
        return;
      }

      if (!inputValue && !Array.isArray(selected)) {
        onChange?.({ value: "", label: "" } as ComboboxOption);
        setSelected([]);
        return;
      }

      const hasMatch = options.some(
        (option) =>
          isRegularOption(option) &&
          (option.label === inputValue || option.value === inputValue),
      );

      if (hasMatch || selected.length > 0) {
        const label = getOptionLabel(selected[0]) || inputValue;
        setInputValue(label);
        onValueChange?.(label);
      } else {
        setInputValue("");
        onValueChange?.("");
        onChange?.({ value: "", label: "" });
        setSelected([]);
      }
    },
    [loading, multiple, inputValue, options, selected, onChange, onValueChange],
  );

  // Sync effects
  useEffect(() => {
    const newSelected = getSelectedOptions(value);
    setSelected(newSelected);
  }, [value, getSelectedOptions]);

  useEffect(() => {
    if (!multiple) {
      const newInputValue =
        getOptionLabel(selected[0]) || value?.toString() || "";
      setInputValue(newInputValue);
    }
  }, [selected, multiple, value]);

  useEffect(() => setOpen(openProp), [openProp]);
  useEffect(() => onOpenChange?.(open), [open, onOpenChange]);

  // Scroll input into view when opened (especially on mobile when keyboard appears)
  useEffect(() => {
    if (open && inputRef && "current" in inputRef && inputRef.current) {
      // Small delay to allow keyboard to appear first
      setTimeout(() => {
        inputRef.current?.scrollIntoView({
          behavior: "smooth",
          block: "center",
        });
      }, 300);
    }
  }, [open, inputRef]);

  // Handle pending blur check
  useEffect(() => {
    if (pendingBlurCheck && !loading) {
      setPendingBlurCheck(false);
      validateInput();
    }
  }, [pendingBlurCheck, loading, validateInput]);

  // Update highlighted index based on input (only when input or options change)
  useEffect(() => {
    if (justSelected.current) {
      justSelected.current = false;
      return;
    }
    const lowerInput = inputValue.toLowerCase();

    // Try exact match first, then fuzzy match
    let index = matchingIndex;

    if (index === -1) {
      index = options.findIndex((option) => {
        if (!isSelectableOption(option)) return false;
        if (!isRegularOption(option)) return false;
        const { label, subLabel, value } = option;
        return (
          label.toLowerCase().includes(lowerInput) ||
          subLabel?.toLowerCase().includes(lowerInput) ||
          value?.toString().toLowerCase().includes(lowerInput)
        );
      });
    }

    // If we found a match, use it. Otherwise, reset to first selectable option
    if (index > -1) {
      setHighlightedIndex(index);
    } else {
      // No match found - reset to first selectable option so navigation works
      const firstSelectable = navigationHelpers.findFirst();
      if (firstSelectable > -1) {
        setHighlightedIndex(firstSelectable);
      }
    }
  }, [inputValue, options, navigationHelpers, matchingIndex]);

  // Separate effect to validate highlighted index (only when options change)
  useEffect(() => {
    if (!isSelectableOption(options[highlightedIndex])) {
      const firstSelectable = navigationHelpers.findFirst();
      if (firstSelectable > -1) setHighlightedIndex(firstSelectable);
    }
  }, [options, navigationHelpers, highlightedIndex]);

  // Event handlers
  const handleFocus = useCallback(() => {
    setOpen(true);
    setIsFocusVisible(true);
  }, []);
  const handleClick = useCallback(() => {
    setOpen((e) => (enableSearch ? true : !e));
    setIsFocusVisible(true);
  }, [enableSearch]);

  const handleBlur = useCallback(() => {
    setIsFocusVisible(false);
    validateInput(true);
  }, [validateInput]);

  const handleInputChange = useCallback(
    (event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
      const newValue = event.target.value;
      setInputValue(newValue || "");
      onValueChange?.(newValue);
      setOpen(true);

      if (!newValue && !multiple) {
        onChange?.({ value: "", label: "" });
        setSelected([]);
      }
    },
    [multiple, onChange, onValueChange],
  );

  const handleOptionSelect = useCallback(
    (option: ComboboxOption) => {
      if (!isRegularOption(option)) return;

      const newHighlightedIndex = options.findIndex(
        (opt) => isRegularOption(opt) && opt.value === option.value,
      );

      if (newHighlightedIndex !== -1) {
        setHighlightedIndex(newHighlightedIndex);
      }

      setTimeout(() => {
        if (multiple) {
          const isSelected = selected.some(
            (item) => isRegularOption(item) && item.value === option.value,
          );
          const newSelected = isSelected
            ? selected.filter(
                (item) =>
                  !(isRegularOption(item) && item.value === option.value),
              )
            : [...selected, option];
          setSelected(newSelected);
          onChange?.(newSelected);
          justSelected.current = true;
          setInputValue("");
          onValueChange?.("");
        } else {
          setSelected([option]);
          onChange?.(option);
          setOpen(false);
        }
      });
    },
    [multiple, selected, onChange, onValueChange, options],
  );

  const handleRemoveOption = useCallback(
    (optionToRemove: ComboboxOption) => {
      if (!isRegularOption(optionToRemove)) return;

      const newSelected = selected.filter(
        (item) =>
          !(isRegularOption(item) && item.value === optionToRemove.value),
      );
      setSelected(newSelected);
      onChange?.(
        multiple ? newSelected : newSelected[0] || { value: "", label: "" },
      );
    },
    [selected, multiple, onChange],
  );

  const selectHighlightedOption = useCallback(() => {
    const match = options.find(
      (option) =>
        isRegularOption(option) &&
        !option.disabled &&
        option.label.toLowerCase() === inputValue.toLowerCase(),
    );
    if (match) {
      handleOptionSelect(match);
    } else {
      const label = getOptionLabel(selected[0]);
      setInputValue(label);
      onValueChange?.(label);
    }
  }, [options, inputValue, selected, onValueChange, handleOptionSelect]);

  const handleKeyDown = useCallback(
    (event: React.KeyboardEvent) => {
      if (props.disabled) return;

      const keyActions: Record<string, () => void> = {
        // biome-ignore lint/nursery/noNestedComponentDefinitions: this is a keydown handler not a component
        Backspace: () => {
          if (multiple && !inputValue && selected.length > 0) {
            event.preventDefault();
            handleRemoveOption(selected[selected.length - 1]);
          }
        },
        // biome-ignore lint/nursery/noNestedComponentDefinitions: this is a keydown handler not a component
        Enter: () => {
          event.preventDefault();
          const option = options[highlightedIndex];
          if (open && isSelectableOption(option)) {
            handleOptionSelect(option);
          }
        },
        // biome-ignore lint/nursery/noNestedComponentDefinitions: this is a keydown handler not a component
        Tab: () => {
          selectHighlightedOption();
          setOpen(false);
        },
        // biome-ignore lint/nursery/noNestedComponentDefinitions: this is a keydown handler not a component
        Escape: () => {
          const highlightedOption = options[highlightedIndex];
          if (
            isRegularOption(highlightedOption) &&
            inputValue === highlightedOption.label
          ) {
            handleOptionSelect(highlightedOption);
          } else {
            const label = getOptionLabel(selected[0]);
            setInputValue(label);
            onValueChange?.(label);
          }
          setOpen(false);
        },
        // biome-ignore lint/nursery/noNestedComponentDefinitions: this is a keydown handler not a component
        ArrowDown: () => {
          event.preventDefault();
          if (!open) {
            setOpen(true);
          } else {
            const nextIndex = navigationHelpers.findNext(highlightedIndex);
            if (nextIndex > -1) setHighlightedIndex(nextIndex);
            onChange?.(options[nextIndex]);
          }
        },
        // biome-ignore lint/nursery/noNestedComponentDefinitions: this is a keydown handler not a component
        ArrowUp: () => {
          event.preventDefault();
          if (open) {
            const prevIndex = navigationHelpers.findPrev(highlightedIndex);
            if (prevIndex > -1) setHighlightedIndex(prevIndex);
            onChange?.(options[prevIndex]);
          }
        },
        // biome-ignore lint/nursery/noNestedComponentDefinitions: this is a keydown handler not a component
        Home: () => {
          if (open) {
            event.preventDefault();
            const firstIndex = navigationHelpers.findFirst();
            if (firstIndex > -1) setHighlightedIndex(firstIndex);
          }
        },
        // biome-ignore lint/nursery/noNestedComponentDefinitions: this is a keydown handler not a component
        End: () => {
          if (open) {
            event.preventDefault();
            const lastIndex = navigationHelpers.findLast();
            if (lastIndex > -1) setHighlightedIndex(lastIndex);
          }
        },
      };

      keyActions[event.key]?.();
    },
    [
      props.disabled,
      multiple,
      inputValue,
      selected,
      open,
      highlightedIndex,
      options,
      handleRemoveOption,
      handleOptionSelect,
      onValueChange,
      navigationHelpers,
      selectHighlightedOption,
    ],
  );

  // Scroll management
  const scrollToHighlighted = useCallback(
    (behavior: ScrollBehavior = "smooth") => {
      if (highlightedIndex >= 0 && listRef.current) {
        const element = listRef.current.children[
          highlightedIndex
        ] as HTMLElement;
        element?.scrollIntoView({ block: "center", behavior });
      }
    },
    [highlightedIndex],
  );

  useEffect(() => scrollToHighlighted(), [scrollToHighlighted]);
  // biome-ignore lint/correctness/useExhaustiveDependencies: scrollToHighlighted should only be called when open is true
  useEffect(() => {
    if (open) setTimeout(() => scrollToHighlighted("instant"));
  }, [open]);

  const handleClearInput = useCallback(() => {
    setInputValue("");
    onValueChange?.("");
    if (!multiple) {
      onChange?.({ value: "", label: "" });
      setSelected([]);
    }
  }, [multiple, onChange, onValueChange]);

  const { trailingIcon, trailingIconProps } = useMemo(() => {
    const icon =
      trailingIconProp ||
      (inputValue.length > 0 && isFocusVisible && enableSearch
        ? "x-md"
        : options.length > 0
          ? "arrowhead-down-md"
          : undefined);

    const iconProps = {
      tabIndex: -1,
      ...trailingIconPropsProp,
      ...(inputValue.length > 0 &&
        isFocusVisible &&
        enableSearch &&
        !trailingIconProp &&
        !trailingIconPropsProp?.onClick && {
          onClick: handleClearInput,
        }),
    };

    return { trailingIcon: icon, trailingIconProps: iconProps };
  }, [
    trailingIconProp,
    inputValue.length,
    isFocusVisible,
    options.length,
    trailingIconPropsProp,
    handleClearInput,
    enableSearch,
  ]);

  // Render option content
  const renderOption = (option: ComboboxOption, index: number) => {
    if (isDividerOption(option)) {
      return <hr className="w-full border-t border-border-subdued my-1 mx-2" />;
    }

    if (!isRegularOption(option)) return null;

    const isHighlighted = highlightedIndex === index;
    const isSelected = selected.some(
      (item) => isRegularOption(item) && item.value === option.value,
    );

    return (
      <>
        {option.leadingIcon && (
          <Icon
            name={option.leadingIcon}
            className={cn(
              "h-5 w-5 shrink-0 text-icon-default group-hover/option:text-[var(--primary-800)]",
              isHighlighted && "text-[var(--primary-800)]",
              classNames?.optionLeadingIcon,
            )}
            aria-hidden="true"
          />
        )}
        <div className="flex flex-col">
          <span>{option.label || option.value}</span>
          {option.subLabel && (
            <span
              className={cn(
                "text-sm text-text-subdued group-hover/option:text-[var(--primary-800)]",
                isHighlighted && "text-[var(--primary-800)]",
              )}
            >
              {option.subLabel}
            </span>
          )}
        </div>
        {isSelected && (
          <Icon
            name="checkmark-md"
            className="shrink-0 ms-auto h-5 w-5"
            aria-hidden="true"
          />
        )}
      </>
    );
  };

  if (asModal && isMobile) {
    return (
      <ModalButton
        variant="basic"
        pillShaped={false}
        className={cn("w-full justify-start", props.className)}
        leadingIcon={<>{leadingIcon}</>}
        trailingIcon="arrowhead-down-lg"
        text={
          multiple
            ? selected.length > 0
              ? selected
                  .map((s) => (isRegularOption(s) ? s.label : ""))
                  .join(", ")
              : value || placeholder
            : selected.length > 0 && isRegularOption(selected[0])
              ? selected[0].label
              : value || placeholder
        }
        size="lg"
        disabled={props.disabled}
        options={{
          title: placeholder || props.label || t("global:select"),
          swipe: true,
        }}
      >
        <div className="px-3 pb-5">
          {options.map((option) => {
            if (isDividerOption(option)) {
              return (
                <div
                  key={`divider-${Math.random()}`}
                  className="flex flex-col w-full"
                >
                  <Seperator
                    className="w-[calc(100%-20px)] ml-2.5 my-0 py-0"
                    orientation="horizontal"
                  />
                </div>
              );
            }

            if (!isRegularOption(option)) return null;

            return (
              <div
                key={
                  option.value ||
                  `item-${option.label}-${option.subLabel || ""}`
                }
                className="flex flex-col w-full"
              >
                <ListItem
                  leadingIcon={option.leadingIcon}
                  primaryContent={option.label}
                  secondaryContent={option.subLabel}
                  classNames={{
                    base: "h-12",
                  }}
                  onClick={() => {
                    handleOptionSelect(option);
                    setTimeout(() => closeModal(), 300);
                  }}
                />
                <Seperator
                  className="w-[calc(100%-20px)] ml-2.5 my-0 py-0"
                  orientation="horizontal"
                />
              </div>
            );
          })}
        </div>
      </ModalButton>
    );
  }

  return (
    <Root
      open={options.length > 0 && open}
      onOpenChange={(isOpen) => {
        if (!isOpen) selectHighlightedOption();
        setOpen(isOpen);
      }}
    >
      <div className="relative w-full">
        <Trigger
          asChild
          onClick={(e) => {
            const target = e.target as HTMLElement;
            if (target.tagName === "LABEL") {
              inputRef.current?.focus();
            }
            e.preventDefault();
          }}
        >
          <div className="flex items-center w-full">
            <Input
              {...props}
              ref={inputRef}
              leadingIcon={leadingIcon}
              trailingIcon={trailingIcon}
              trailingIconProps={trailingIconProps}
              value={inputValue}
              onChange={handleInputChange}
              onKeyDown={handleKeyDown}
              onFocus={handleFocus}
              onMouseDown={handleClick}
              onBlur={handleBlur}
              readOnly={!enableSearch}
              autoComplete="off"
              role="combobox"
              placeholder={selected.length ? "" : placeholder}
              aria-controls={listboxId}
              aria-expanded={open}
              aria-haspopup="listbox"
              aria-autocomplete="list"
              data-testid="combobox-input"
              aria-activedescendant={
                open &&
                highlightedIndex >= 0 &&
                highlightedIndex < options.length
                  ? `${listboxId}-option-${highlightedIndex}`
                  : undefined
              }
              prepend={
                multiple &&
                selected.length > 0 && (
                  <div className="contents">
                    {selected.map((option) => {
                      if (!isRegularOption(option)) return null;
                      return (
                        <Tag
                          key={option.value}
                          text={option.label}
                          color={option.color || "grey"}
                          removeIcon={true}
                          handleRemoveClick={() => handleRemoveOption(option)}
                          className={cn(
                            "whitespace-nowrap truncate",
                            props.labelOutside
                              ? "h-6 text-sm"
                              : props.size !== "md" || props.labelOutside
                                ? "h-5 text-xs"
                                : "h-4 text-xs",
                          )}
                        />
                      );
                    })}
                  </div>
                )
              }
              classNames={{
                ...classNames,
                inputContainer: cn(
                  classNames?.inputContainer,
                  !enableSearch && "cursor-pointer",
                ),
                input: cn(
                  classNames?.input,
                  !enableSearch && "cursor-pointer select-none",
                ),
                inputWrapper: cn(
                  classNames?.inputWrapper,
                  "flex-wrap",
                  props.size === "md" && !props.labelOutside && "gap-y-px",
                ),
              }}
            />
          </div>
        </Trigger>

        <Portal>
          <Content
            className={cn(
              popoverContentVariants({ isEmpty: options.length === 0 }),
              !loading && options.length === 0 && "opacity-0",
            )}
            sideOffset={4}
            align="start"
            onOpenAutoFocus={(e) => e.preventDefault()}
            onCloseAutoFocus={(e) => e.preventDefault()}
            ref={dropdownRef}
            collisionPadding={8}
            avoidCollisions={false}
          >
            <div
              // biome-ignore lint/a11y/useSemanticElements: Custom combobox implementation follows WAI-ARIA pattern
              role="listbox"
              ref={listRef}
              className="group/listbox max-h-[min(calc(var(--radix-popover-content-available-height)-16px),340px)] overflow-auto p-1"
              id={listboxId}
              aria-label={props.label || "Options"}
              tabIndex={-1}
            >
              {loading ? (
                <div className="p-5 text-center text-sm text-text-subdued flex justify-center items-center w-full">
                  <Icon name="loading" aria-hidden="true" />
                  <span className="sr-only">{t("global:loading")}</span>
                </div>
              ) : (
                options.map((option, index) => {
                  const isSelected = selected.some(
                    (item) =>
                      isRegularOption(item) &&
                      isRegularOption(option) &&
                      item.value === option.value,
                  );
                  return (
                    // biome-ignore lint/a11y/noStaticElementInteractions: this is a custom combobox implementation
                    // biome-ignore lint/a11y/useAriaPropsSupportedByRole: this is a custom combobox implementation
                    <div
                      key={Object.values(option).join("-")}
                      role={isDividerOption(option) ? "separator" : "option"}
                      id={`${listboxId}-option-${index}`}
                      className={cn(
                        optionVariants({
                          disabled: isRegularOption(option)
                            ? option.disabled
                            : false,
                          highlighted:
                            !isDividerOption(option) &&
                            highlightedIndex === index,
                          divider: isDividerOption(option),
                        }),
                      )}
                      onMouseDown={(e) => e.preventDefault()}
                      onClick={(e) => {
                        if (
                          isSelectableOption(option) &&
                          isRegularOption(option)
                        ) {
                          option.onClick?.(e);
                          if (!option.onClick || !e.defaultPrevented) {
                            handleOptionSelect(option);
                          }
                        }
                      }}
                      onKeyDown={(e) => {
                        if (
                          (e.key === "Enter" || e.key === " ") &&
                          isSelectableOption(option)
                        ) {
                          e.preventDefault();
                          handleOptionSelect(option);
                        }
                      }}
                      aria-selected={isSelected}
                      aria-disabled={!isSelectableOption(option)}
                      tabIndex={-1}
                    >
                      {renderOption(option, index)}
                    </div>
                  );
                })
              )}
            </div>
          </Content>
        </Portal>
      </div>
    </Root>
  );
};
